// src/pages/admin/WeeklyCourseActivityChart.jsx
import React, { useContext, useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend
} from "recharts";
import { DContext } from "../../../context/Datacontext";

export default function WeeklyCourseActivityChart() {
    const { BeURL } = useContext(DContext);
    const [data, setData] = useState([]);
    const [range, setRange] = useState("week"); // week | month | year | custom
    const [start, setStart] = useState(""); // yyyy-mm-dd
    const [end, setEnd] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async (r = range, s = start, e = end) => {
        try {
            setLoading(true);
            setError(null);

            let url = `${BeURL}/weekly-course-activity-toinstructor?range=${r}`;

            if (r === "custom") {
                if (!s || !e) {
                    setError("Please select start and end dates for custom range.");
                    setLoading(false);
                    return;
                }
                url += `&start=${encodeURIComponent(s)}&end=${encodeURIComponent(e)}`;
            }
            const res = await fetch(url, {
                method: "GET",
                credentials: "include"  
            });
             
            const json = await res.json();
            if (!res.ok) {
                setError(json.message || "Server error");
                setLoading(false);
                return;
            }
            setData(json.data || []);
        } catch (err) {
            console.error(err);
            setError("Network error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // fetch when component mounts and when range changes (but not on typing custom dates)
        if (range !== "custom") fetchData(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BeURL, range]);

    const onApplyCustom = () => {
        fetchData("custom", start, end);
    };

    return (
        <div className="w-full">
            {/* Controls (do not change chart design below) */}
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                <select
                    value={range}
                    onChange={(e) => setRange(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                >
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="custom">Custom Range</option>
                </select>

                {range === "custom" && (
                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                        <input
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm"
                        />
                        <span className="text-sm text-gray-500">to</span>
                        <input
                            type="date"
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm"
                        />
                        <button
                            onClick={onApplyCustom}
                            className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm"
                        >
                            Apply
                        </button>
                    </div>
                )}

                {/* refresh button */}
                <button
                    onClick={() => fetchData(range, start, end)}
                    className="ml-auto sm:ml-4 mt-2 sm:mt-0 px-3 py-2 border rounded-md text-sm"
                >
                    Refresh
                </button>
            </div>

            {/* error / loading */}
            {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
            {loading && <div className="text-sm text-gray-500 mb-2">Loading...</div>}

            {/* KEEP CHART DESIGN EXACT — only data changes */}
            <div className="h-64 rounded-lg overflow-hidden flex items-center justify-center">
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data} barGap={6} barCategoryGap={20}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 13 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 13 }} axisLine={false} tickLine={false} />
                        <Tooltip
                            cursor={{ fill: "rgba(0,0,0,0.05)" }}
                            contentStyle={{
                                borderRadius: "10px",
                                border: "none",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.1)"
                            }}
                        />
                        <Legend verticalAlign="bottom" height={40} iconType="circle" />
                        <Bar dataKey="orders" fill="#22c55e" radius={[6, 6, 0, 0]} barSize={18} />
                        <Bar dataKey="cart" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={18} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}